import type { SavedSearchDTO } from "@shaarp/common";

/**
 * What the people table panel shows, from where its rows stand.
 *
 * The rows a run streams are replaced on each new one: a reveal, a list or a
 * CRM sync — paid — clicked on them would not last. So the row actions and the
 * cache writer are tied to the saved rows themselves, not to « still filling »:
 * a failed read of the saved search ends the filling without ever unlocking
 * them.
 */
export type PeopleTablePanelState = {
  /** The run watched from this panel is still filling the table. */
  readonly isFilling: boolean;
  /** The saved search could not be read · not read yet · the table. */
  readonly body: "error" | "skeleton" | "table";
  /** Reveals, lists and CRM syncs are refused on the rows. */
  readonly areRowActionsLocked: boolean;
  /** A change to a row is written into the cached saved search. */
  readonly areRowChangesSaved: boolean;
};

/**
 * The panel's state.
 *
 * `hasWatchedRun`: the panel saw the search run (a table opened from its card
 * once finished has not, and is a plain load). `isSaved`: its saved rows are
 * read, and final (`isSavedPeopleTableSettled`). `isError`: reading them
 * failed.
 */
export function getPeopleTablePanelState({
  hasWatchedRun,
  isSaved,
  isError,
}: {
  readonly hasWatchedRun: boolean;
  readonly isSaved: boolean;
  readonly isError: boolean;
}): PeopleTablePanelState {
  // The search itself, then the request reading its saved rows back.
  const isFilling = hasWatchedRun && !isSaved && !isError;
  const body = isError
    ? "error"
    : !isSaved && !isFilling
      ? "skeleton"
      : "table";
  return {
    isFilling,
    body,
    areRowActionsLocked: !isSaved,
    areRowChangesSaved: isSaved,
  };
}

/** How often the panel reads a saved search back while its run has not settled. */
export const SAVED_PEOPLE_TABLE_POLL_MS = 2_000;

/**
 * The longest a people search run lasts, its rows persisted included: the
 * API's `PEOPLE_SEARCH_MAX_RUN_MS` (`api/src/common/constants/people-table.ts`).
 */
const PEOPLE_SEARCH_MAX_RUN_MS = 210_000;

/**
 * Reads of a saved search that has not settled before the panel takes it as
 * it is. A row still `streaming` past the longest run belongs to a run that
 * died with its server: nothing will ever write its rows.
 */
export const SAVED_PEOPLE_TABLE_MAX_READS = Math.ceil(
  PEOPLE_SEARCH_MAX_RUN_MS / SAVED_PEOPLE_TABLE_POLL_MS,
);

/**
 * Whether the saved search holds the rows of its run, final — the rows the
 * panel reads, and the only ones its paid actions may land on.
 *
 * The saved search exists as `streaming` from the run's first moment, with no
 * rows: the server writes them once, as the run ends (`completeSearch`). A
 * Stop hands the panel over to the saved search right away, before that
 * write, and an empty read then would wipe the rows the user watched fill
 * in. The status is also written a moment before the rows, so a settled read
 * with no rows where the run watched from here found people is one that
 * landed in between.
 *
 * `readCount`: the reads made so far, which bound the wait.
 * `watchedPeopleCount`: the people the run watched from this panel had found,
 * 0 for a table opened once finished.
 */
export function isSavedPeopleTableSettled({
  saved,
  readCount,
  watchedPeopleCount,
}: {
  readonly saved: Pick<SavedSearchDTO, "status" | "employees"> | undefined;
  readonly readCount: number;
  readonly watchedPeopleCount: number;
}): boolean {
  if (!saved) return false;
  if (readCount >= SAVED_PEOPLE_TABLE_MAX_READS) return true;
  if (saved.status === "streaming") return false;
  return watchedPeopleCount === 0 || saved.employees.length > 0;
}
